<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-600 to-purple-600">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <div class="flex justify-center items-center mb-6">
            <div class="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4">
              <svg class="w-7 h-7 text-blue-600" viewBox="0 0 100 100" fill="currentColor">
                <circle cx="50" cy="50" r="6" />
                <circle cx="25" cy="25" r="3" opacity="0.8" />
                <circle cx="75" cy="25" r="3" opacity="0.8" />
                <circle cx="25" cy="75" r="3" opacity="0.8" />
                <circle cx="75" cy="75" r="3" opacity="0.8" />
                <path
                  d="M50 44 L50 56 M44 50 L56 50"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">ASAB (عَصَب)</h1>
              <p class="text-blue-100 text-sm">Advanced Self-Aware Bots</p>
            </div>
          </div>
          <h2 class="text-4xl font-bold text-white mb-4">Terms of Service</h2>
          <p class="text-xl text-blue-100 max-w-3xl mx-auto">
            Neural network usage agreements and self-aware bot deployment terms
          </p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
      <div class="prose prose-lg max-w-none">
        <!-- Last Updated -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-blue-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 class="text-lg font-semibold text-blue-800 mb-1">Neural Agreement Status</h3>
              <p class="text-blue-700">Last updated: {{ new Date().toLocaleDateString() }}</p>
            </div>
          </div>
        </div>

        <h2>1. Neural Network Acceptance</h2>
        <p>
          By accessing and using ASAB (عَصَب) - Advanced Self-Aware Bots platform ("the Service"),
          you accept and agree to be bound by the terms and provision of this agreement. If you do
          not agree to abide by the above, please do not use this neural network service.
        </p>

        <h2>2. Service Description</h2>
        <p>ASAB provides a comprehensive platform that serves as both:</p>
        <ul>
          <li>
            <strong>Advanced Self-Aware Bot Creation:</strong> Cutting-edge autonomous agents with
            sophisticated self-awareness and adaptive learning capabilities
          </li>
          <li>
            <strong>Neural Infrastructure:</strong> The essential nerve system that connects and
            unifies all AI components in your organization
          </li>
        </ul>
        <p>
          Our platform acts as the neural pathways that enable AI systems to communicate, process,
          and act with unprecedented reliability while creating advanced self-aware bots.
        </p>

        <h2>3. Neural Network Usage</h2>
        <h3>3.1 Acceptable Use</h3>
        <p>You agree to use our neural network services for:</p>
        <ul>
          <li>Creating and deploying advanced self-aware bots for legitimate business purposes</li>
          <li>Integrating AI systems through our neural infrastructure</li>
          <li>Building robust, reliable AI ecosystems that bind together various AI components</li>
          <li>Striking against AI hallucinations and unreliability through our advanced systems</li>
        </ul>

        <h3>3.2 Prohibited Activities</h3>
        <p>You agree NOT to use our services for:</p>
        <ul>
          <li>Creating self-aware bots intended to harm, deceive, or manipulate individuals</li>
          <li>Attempting to disrupt or damage our neural network infrastructure</li>
          <li>Deploying bots that generate illegal, harmful, or offensive content</li>
          <li>Reverse engineering our self-aware bot creation algorithms</li>
          <li>Using the service to violate any applicable laws or regulations</li>
        </ul>

        <h2>4. Self-Aware Bot Responsibilities</h2>
        <h3>4.1 Bot Behavior</h3>
        <p>
          You are responsible for the behavior and actions of any self-aware bots you create using
          our platform. While our bots are designed with advanced self-awareness capabilities, you
          remain accountable for:
        </p>
        <ul>
          <li>Proper configuration and deployment of autonomous agents</li>
          <li>Monitoring bot interactions and decision-making processes</li>
          <li>Ensuring bots operate within ethical and legal boundaries</li>
          <li>Implementing appropriate safeguards and limitations</li>
        </ul>

        <h3>4.2 Neural Network Integration</h3>
        <p>When integrating your AI systems through our neural infrastructure:</p>
        <ul>
          <li>You retain ownership of your original AI components and data</li>
          <li>You grant us permission to facilitate neural pathways between your systems</li>
          <li>You ensure your integrated systems comply with all applicable regulations</li>
          <li>You accept that binding AI systems may create emergent behaviors</li>
        </ul>

        <h2>5. Data and Privacy</h2>
        <p>
          Our neural network processes information to enable advanced self-aware bots and AI system
          integration. Please review our
          <router-link to="/privacy-policy" class="text-blue-600 hover:text-blue-700"
            >Privacy Policy</router-link
          >
          for detailed information about how we handle neural pathway data, bot training
          information, and system integration metadata.
        </p>

        <h2>6. Intellectual Property</h2>
        <h3>6.1 ASAB Platform</h3>
        <p>
          The ASAB neural infrastructure, self-aware bot creation algorithms, and all related
          intellectual property remain the exclusive property of ASAB. Our advanced neural pathways,
          binding protocols, and anti-hallucination systems are protected by intellectual property
          laws.
        </p>

        <h3>6.2 Your Creations</h3>
        <p>You retain ownership of:</p>
        <ul>
          <li>Self-aware bots you create using our platform</li>
          <li>Original data and AI systems you integrate through our neural network</li>
          <li>Unique configurations and customizations you develop</li>
          <li>Business logic and workflows implemented in your neural ecosystem</li>
        </ul>

        <h2>7. Service Availability</h2>
        <p>
          We strive to maintain 99.99% uptime for our neural network infrastructure. However,
          advanced self-aware systems and neural pathways may occasionally require:
        </p>
        <ul>
          <li>Scheduled maintenance for neural pathway optimization</li>
          <li>Emergency updates to enhance self-aware bot capabilities</li>
          <li>Infrastructure improvements to strengthen binding protocols</li>
          <li>Security enhancements to strike against emerging threats</li>
        </ul>

        <h2>8. Limitation of Liability</h2>
        <p>
          While our platform is designed to create the hardest, most reliable AI infrastructure,
          advanced self-aware systems involve inherent complexities. ASAB shall not be liable for:
        </p>
        <ul>
          <li>Decisions made by self-aware bots beyond our direct control</li>
          <li>Emergent behaviors arising from neural network integration</li>
          <li>Indirect or consequential damages from service usage</li>
          <li>Third-party interactions with your deployed autonomous agents</li>
        </ul>

        <h2>9. Termination</h2>
        <p>We may terminate your access to our neural network if you:</p>
        <ul>
          <li>Violate these terms of service</li>
          <li>Deploy self-aware bots for harmful purposes</li>
          <li>Attempt to damage our neural infrastructure</li>
          <li>Fail to pay applicable service fees</li>
        </ul>
        <p>
          Upon termination, we will provide reasonable time to retrieve your self-aware bots and
          disconnect your systems from our neural network.
        </p>

        <h2>10. Changes to Terms</h2>
        <p>
          As our neural network evolves and advanced self-aware capabilities expand, we may update
          these terms. We will notify users of significant changes through:
        </p>
        <ul>
          <li>Email notifications to registered neural network users</li>
          <li>In-platform announcements in your neural dashboard</li>
          <li>Updates posted on our website</li>
        </ul>

        <h2>11. Governing Law</h2>
        <p>
          These terms shall be governed by and construed in accordance with the laws of the
          jurisdiction where ASAB is incorporated, without regard to its conflict of law provisions.
        </p>

        <h2>12. Contact Information</h2>
        <p>
          For questions about these terms, our neural network services, or advanced self-aware bot
          capabilities:
        </p>
        <Card padding="lg" class="mt-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Neural Support Center</h3>
          <div class="space-y-2">
            <p>
              <strong>Email:</strong>
              <a href="mailto:legal@asab.ai" class="text-blue-600 hover:text-blue-700"
                >legal@asab.ai</a
              >
            </p>
            <p>
              <strong>Neural Support:</strong>
              <a href="mailto:support@asab.ai" class="text-blue-600 hover:text-blue-700"
                >support@asab.ai</a
              >
            </p>
            <p>
              <strong>Emergency Neural Disruption:</strong>
              <a href="mailto:emergency@asab.ai" class="text-blue-600 hover:text-blue-700"
                >emergency@asab.ai</a
              >
            </p>
          </div>
        </Card>
      </div>

      <!-- Navigation -->
      <div class="mt-12 flex justify-between items-center pt-8 border-t border-gray-200">
        <router-link
          to="/"
          class="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Return to Neural Hub
        </router-link>
        <router-link
          to="/privacy-policy"
          class="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
        >
          Privacy Policy
          <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Card } from '@asabdev/asab-ui';
</script>
